import { html, TemplateResult, PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import { userState } from "@/state/Users";
import { annotationState } from "@/state/Annotations";
import { userAnnotationState } from "@/state/UserAnnotations";
import { machineAnnotationState, type MachineAnnotationData } from "@/state/MachineAnnotations";
import { setupAnnotationContext } from "@/state/AnnotationContext";
import { highlightCode } from "@/feedback/code-highlight";
import { escapeHtml } from "@/utilities";
import codeListing from "@/code_listing";
import "@/components/annotations/annotation_options";
import "@/components/annotations/code_listing_row";
import "@/components/copy_button";
import type { MachineAnnotation, FeedbackEnvelopeContext } from "@/types/FeedbackResult";

// Only attach the document-level annotation event listeners once per page.
let annotateButtonsAttached = false;

// Shared stable empty array for lines without machine annotations: a fresh `[]`
// per line per render would change the prop identity and re-render every line.
const NO_MACHINE_ANNOTATIONS: MachineAnnotation[] = [];

/**
 * Renders the code tab of the feedback table: syntax-highlighted submission code
 * with the Dodona annotation system wired up.
 *
 * @element d-feedback-code-tab
 *
 * @prop {MachineAnnotation[]}     annotations         - Machine annotations to overlay on the code.
 * @prop {string|null}             code                - Submission source code.
 * @prop {string|null}             programmingLanguage - Language for syntax highlighting.
 * @prop {FeedbackEnvelopeContext} submissionContext   - Submission context (annotation setup, user state).
 */
@customElement("d-feedback-code-tab")
export class FeedbackCodeTab extends DodonaElement {
    @property({ type: Array }) annotations: MachineAnnotation[] = [];
    @property({ type: String }) code: string | null = null;
    @property({ type: String }) programmingLanguage: string | null = null;
    @property({ type: Object }) submissionContext: FeedbackEnvelopeContext = {};

    @state() private _lines: string[] = [];
    // Identifies the latest highlight request so stale async results can be discarded.
    private _highlightRequestKey = "";
    // The inputs the annotation context was last set up for. connectedCallback and the first
    // updated() both see the initial properties, so without this the setup ran twice and
    // fetched the annotations twice per submission.
    private _annotationStateInputs: [FeedbackEnvelopeContext, string | null] | null = null;

    override connectedCallback(): void {
        super.connectedCallback();
        this._initAnnotationState();
    }

    override willUpdate(changedProperties: PropertyValues): void {
        super.willUpdate(changedProperties);
        if (changedProperties.has("code") || changedProperties.has("programmingLanguage")) {
            this._highlight();
        }
    }

    override updated(changedProperties: PropertyValues): void {
        super.updated(changedProperties);
        if (changedProperties.has("annotations")) {
            machineAnnotationState.setMachineAnnotations(this.annotations as MachineAnnotationData[]);
        }
        if (
            changedProperties.has("code") ||
            changedProperties.has("programmingLanguage") ||
            changedProperties.has("submissionContext")
        ) {
            this._initAnnotationState();
        }
    }

    private _initAnnotationState(): void {
        const [context, code] = this._annotationStateInputs ?? [];
        if (context === this.submissionContext && code === this.code) {
            return;
        }
        this._annotationStateInputs = [this.submissionContext, this.code];

        setupAnnotationContext({
            submissionId: this.submissionContext.submissionId ?? undefined,
            courseId: this.submissionContext.courseId ?? undefined,
            exerciseId: this.submissionContext.exerciseId ?? undefined,
            userId: this.submissionContext.userId ?? undefined,
            code: this.code ?? "",
            userIsStudent: this.submissionContext.userIsStudent ?? false,
            canSubmitAsOwn: this.submissionContext.canSubmitAsOwn ?? false,
        });

        if (this.submissionContext.annotationsAllowed) {
            userState.addPermission("annotation.create");
            if (!annotateButtonsAttached) {
                annotateButtonsAttached = true;
                codeListing.initAnnotateButtons();
            }
        }

        if (this.submissionContext.loadUserAnnotations && this.submissionContext.submissionId !== undefined) {
            void userAnnotationState.fetch(this.submissionContext.submissionId);
            annotationState.visibility = "important";
        }
    }

    private _highlight(): void {
        const { code, programmingLanguage } = this;
        if (!code) {
            this._lines = [];
            return;
        }
        // Render immediately with sync-escaped lines; upgrade when async resolves.
        this._lines = code.split("\n").map((l) => escapeHtml(l));

        const requestKey = `${programmingLanguage ?? ""}::${code}`;
        this._highlightRequestKey = requestKey;
        void highlightCode(code, programmingLanguage ?? "").then((highlighted) => {
            if (this._highlightRequestKey === requestKey) {
                this._lines = highlighted.split("\n");
            }
        });
    }

    // Track the hovered row so only that row mounts its create-annotation button.
    private _onRowPointerOver(e: PointerEvent): void {
        const rowEl = (e.target as Element).closest("d-code-listing-row") as (Element & { row: number }) | null;
        const row = rowEl?.row ?? null;
        if (userAnnotationState.hoveredRow !== row) {
            userAnnotationState.hoveredRow = row;
        }
    }

    private _onRowPointerLeave(): void {
        userAnnotationState.hoveredRow = null;
    }

    render(): TemplateResult {
        const { code, submissionContext } = this;
        const submissionId = submissionContext.submissionId;
        const hasAnnotations = this.annotations.length > 0;
        // Raw source lines, passed down so rows/lines/selection layers don't each
        // subscribe to submissionState.codeByLine (which never changes after load).
        const rawLines = code?.split("\n") ?? [];
        // Resolve the create permission once here instead of in every row.
        const canCreateAnnotation = userState.hasPermission("annotation.create");
        // Read question mode once here instead of subscribing every row to it.
        const isQuestionMode = annotationState.isQuestionMode;

        const codeTable = html`
            <div class="code-table" data-submission-id="${submissionId ?? ""}">
                <d-annotation-options></d-annotation-options>
                <div class="code-listing-container">
                    <table
                        class="code-listing highlighter-rouge"
                        @pointerover=${this._onRowPointerOver}
                        @pointerleave=${this._onRowPointerLeave}
                    >
                        <tbody>
                            ${this._lines.map(
                                (lineHtml, i) =>
                                    html`<d-code-listing-row
                                        .row=${i + 1}
                                        .code=${rawLines[i] ?? ""}
                                        .canCreateAnnotation=${canCreateAnnotation}
                                        .isQuestionMode=${isQuestionMode}
                                        .hasMachineAnnotation=${
                                            (machineAnnotationState.byLine.get(i + 1)?.length ?? 0) > 0
                                        }
                                        .machineAnnotationsToMark=${
                                            machineAnnotationState.byMarkedLine.get(i + 1) ?? NO_MACHINE_ANNOTATIONS
                                        }
                                        .renderedCode=${lineHtml}
                                    ></d-code-listing-row>`,
                            )}
                        </tbody>
                    </table>
                    ${code ? html`<d-copy-button .text=${code}></d-copy-button>` : ""}
                </div>
            </div>
        `;

        return hasAnnotations ? html`<div class="linter">${codeTable}</div>` : codeTable;
    }
}
